<template>
  <div class="container mt-4">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4" style="color: #b03a45;">
        <i class="fas fa-layer-group me-2"></i> {{ $t('budgetSubCategory.title') }}
      </h4>

      <!-- Form -->
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('budgetSubCategory.code') }}</label>
              <input type="text" class="form-control" v-model="form.code" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('budgetSubCategory.name') }}</label>
              <input type="text" class="form-control" v-model="form.name" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('budgetSubCategory.budgetCategory') }}</label>
              <select v-model="form.budget_category_id" class="form-select" required>
                <option value="" disabled>{{ $t('budgetSubCategory.selectCategory') }}</option>
                <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                  {{ cat.name }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="d-flex gap-3 mt-3">
          <button type="submit" class="btn text-white px-4" style="background-color: #b03a45;">
            {{ editingId ? $t('budgetSubCategory.update') : $t('budgetSubCategory.save') }}
          </button>
          <button type="button" class="btn btn-secondary px-4" @click="resetForm">{{ $t('budgetSubCategory.cancel') }}</button>
        </div>
      </form>

      <!-- List -->
      <hr class="my-4" />
      <h5 class="mb-3" style="color: #b03a45;">
        <i class="fas fa-list me-2"></i> {{ $t('budgetSubCategory.existingBudgetSubCategories') }}
      </h5>

      <div v-if="subCategories.length" class="table-responsive">
        <table class="table table-bordered align-middle">
          <thead class="table-light">
            <tr>
              <th>{{ $t('budgetSubCategory.code') }}</th>
              <th>{{ $t('budgetSubCategory.name') }}</th>
              <th>{{ $t('budgetSubCategory.category') }}</th>
              <th class="text-center">{{ $t('budgetSubCategory.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="sub in subCategories" :key="sub.id">
              <td>{{ sub.code }}</td>
              <td>{{ sub.name }}</td>
              <td>{{ sub.category?.name || '-' }}</td>
              <td class="text-center">
                <button class="btn btn-outline-danger btn-sm me-2" @click="loadSubCategory(sub)">{{ $t('budgetSubCategory.edit') }}</button>
                <button class="btn btn-outline-secondary btn-sm" @click="deleteSubCategory(sub.id)">{{ $t('budgetSubCategory.delete') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="text-muted">{{ $t('budgetSubCategory.noSubcategories') }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import budgetSubCategoryService from '@/services/budgetSubCategoryService'
import budgetCategoryService from '@/services/budgetCategoryService'

const toast = useToast()
const subCategories = ref([])
const categories = ref([])
const editingId = ref(null)

const form = ref({
  code: '',
  name: '',
  budget_category_id: ''
})

const loadCategories = async () => {
  try {
    const { data } = await budgetCategoryService.getAll()
    categories.value = data
  } catch (e) {
    toast.error($t('budgetSubCategory.failedToLoadCategories'))
  }
}

const loadSubCategories = async () => {
  try {
    const res = await budgetSubCategoryService.getAll()
    subCategories.value = res.data
  } catch (error) {
    toast.error($t('budgetSubCategory.failedToLoadSubcategories'))
    console.error(error)
  }
}

const handleSubmit = async () => {
  try {
    if (editingId.value) {
      await budgetSubCategoryService.update(editingId.value, form.value)
      toast.success($t('budgetSubCategory.subCategoryUpdated'))
    } else {
      await budgetSubCategoryService.create(form.value)
      toast.success($t('budgetSubCategory.subCategoryCreated'))
    }
    resetForm()
    loadSubCategories()
  } catch (e) {
    toast.error($t('budgetSubCategory.failedToSaveSubCategory'))
    console.error(e)
  }
}

const loadSubCategory = (sub) => {
  editingId.value = sub.id
  form.value = {
    code: sub.code,
    name: sub.name,
    budget_category_id: sub.budget_category_id
  }
}

const resetForm = () => {
  editingId.value = null
  form.value = {
    code: '',
    name: '',
    budget_category_id: ''
  }
}

const deleteSubCategory = async (id) => {
  if (!confirm($t('budgetSubCategory.confirmDelete'))) return
  try {
    await budgetSubCategoryService.delete(id)
    subCategories.value = subCategories.value.filter(sub => sub.id !== id)
    toast.success($t('budgetSubCategory.deletedSuccessfully'))
  } catch (error) {
    toast.error($t('budgetSubCategory.failedToDelete'))
    console.error(error)
  }
}

onMounted(() => {
  loadCategories()
  loadSubCategories()
})
</script>
